import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Menu, TextField, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import { FilterIcon } from "../../../../../../../assets/icons";
import { SECTION_ICONS } from "../../../../../../../components/common/FiltersMenu";
import { FilterContexType } from "../../../../../../../components/layout/ViewHeader";
import { useFiltersRegistry } from "../../../../../../../context/FiltersProvider";
import { getRootCauseMenuSections } from "../../../../../../../context/FiltersProvider/selectors/RootCause/menuSelectors";
import { FilterDefinition } from "../../../../../../../context/FiltersProvider/types";
import { RootCauseCardType } from "../../../../data";
import { RootCauseFiltersAccordion } from "../RootCauseFiltersAccordion";

type Props = {
	cardId: RootCauseCardType;
	filterContext: FilterContexType;
	onSelect: (filter: FilterDefinition) => void;
};

export const RootCauseFiltersMenu = ({ cardId, filterContext, onSelect }: Props) => {
	const { t } = useTranslation("common");
	const { registry, rootCauseState } = useFiltersRegistry();

	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const [search, setSearch] = useState("");
	const [expandedSections, setExpandedSections] = useState<string[]>([]);

	const buttonRef = useRef<HTMLButtonElement>(null);

	const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
		setSearch("");
		setExpandedSections([]);
	};

	const sections = useMemo(
		() =>
			getRootCauseMenuSections(registry, cardId, t, {
				visibility: filterContext,
				search,
			}),
		[registry, cardId, filterContext, search]
	);

	const toggleSection = (sectionId: string) => {
		setExpandedSections((prev) =>
			prev.includes(sectionId) ? prev.filter((id) => id !== sectionId) : [...prev, sectionId]
		);
	};

	useEffect(() => {
		if (!search.trim()) {
			setExpandedSections([]);
			return;
		}

		const expanded = sections.filter((section) => section.items.length > 0).map((section) => section.id);

		setExpandedSections(expanded);
	}, [search]);

	const disableMenuOnAdvanceMode = cardId !== "perimeter" && rootCauseState[cardId].advancedMode;

	const isPerimeterCard = cardId === "perimeter";

	return (
		<>
			<Tooltip title={disableMenuOnAdvanceMode ? t("advanced mode enabled text") : ""} placement="top">
				<span>
					<Button
						ref={buttonRef}
						disabled={disableMenuOnAdvanceMode}
						onClick={handleOpen}
						color="primary"
						startIcon={<FilterIcon />}
					>
						{t("filters")}
					</Button>
				</span>
			</Tooltip>

			<Menu
				anchorEl={anchorEl}
				open={Boolean(anchorEl)}
				onClose={handleClose}
				transformOrigin={{
					vertical: "bottom",
					horizontal: "left",
				}}
			>
				<Box
					sx={{
						width: 320,
						maxHeight: 400,
						overflowY: "scroll",
						scrollbarGutter: "stable",
					}}
				>
					<TextField
						sx={{
							pt: 2,
							px: 2,
							pb: 1,
							backgroundColor: "background.paper",
							position: "sticky",
							top: 0,
							zIndex: 10,
						}}
						fullWidth
						size="small"
						value={search}
						onChange={(e) => setSearch(e.target.value)}
					/>

					{sections.map((section) => (
						<RootCauseFiltersAccordion
							key={section.id}
							title={section.title}
							items={section.items}
							expanded={expandedSections.includes(section.id)}
							onToggle={() => toggleSection(section.id)}
							onSelect={(filter) => {
								onSelect(filter);
							}}
							icon={SECTION_ICONS[section.id]}
							isPerimeterCard={isPerimeterCard}
						/>
					))}
				</Box>
			</Menu>
		</>
	);
};
